// src/backendServices/productManagment/api_product.ts

import   axios                         from 'axios';
import { API_URLS                   }  from '../urls';
import { BackendResponse            ,
         handleError                }  from '../../dataTypes/backendServicesManagment/types';

import { Product                    ,
         initializedProduct         ,
         ProductVariant             ,
         initializedProductVariant  ,
         ProductPrice               ,
         initializedPrice           ,
         ProductDiscount            ,
         initializedDiscount        ,
         ProductCharacteristic      ,
         initializedCharacteristic  ,
         ProductMedia               ,
         initializedMedia           }  from '../../dataTypes/productManagment/types';

import { convertDateToString        }  from '../../dataTypes/timeManagment/utils';

import { getUserAgentInformations   }  from '../../utils/utils_userAgent';
import { getAppInformations         }  from '../../utils/utils_app';

export interface BackendResponse_ProductList extends BackendResponse<Product[]> {}

const mapCharacteristics = (rawCharacteristics: any[]): ProductCharacteristic[] =>
  rawCharacteristics.map((rawChar) => ({
    ...initializedCharacteristic(),
    id          : rawChar.id    ?? 0,
    name        : rawChar.name  ?? "",
    kind        : rawChar.kind  ?? 'Dimension',
    unit        : rawChar.unit  ?? "",
    value       : rawChar.value ?? "",
    rank        : rawChar.rank  ?? 0,
	tag         : '',	
	}));

const mapMedias = (rawMedias: any[]): ProductMedia[] =>
  rawMedias.map((rawMedias) => ({
    ...initializedMedia(),
    id              : rawMedias.id              ?? 0,
    rank            : rawMedias.rank            ?? 0,
    name            : rawMedias.name            ?? "",
    publicFileName  : rawMedias.publicFileName  ?? "",
    privateFileName : rawMedias.privateFileName ?? "",
    asThumbnail     : rawMedias.asThumbnail     ?? false,
    mediaType       : rawMedias.mediaType       ?? "",
	tag             : '',
    fileContent     : null,
  }));


const mapDiscounts = (rawDiscounts: any[]): ProductDiscount[] =>
  rawDiscounts.map((rawDiscount) => ({
    ...initializedDiscount(),
    id             : rawDiscount.id               ?? 0,
    type           : rawDiscount.type             ?? '',
    condition      : rawDiscount.condition        ?? '',
    value          : rawDiscount.value            ?? '',
    active         : rawDiscount.active           ?? false,
    priceToApplyOn : rawDiscount.priceToApplyOn   ?? '',
    priceToPay     : rawDiscount.priceToPay / 100 ?? 0,
  }));

const mapPrices = (rawPrices: any[]): ProductPrice[] =>
  rawPrices.map((rawPrice) => ({
    ...initializedPrice(),
    id             :                rawPrice.id                 ?? 0,
    name           :                rawPrice.name               ?? "",
    requiredStatus :                rawPrice.requiredStatus     ?? "",
    comment        :                rawPrice.comment            ?? "",
    code           :                rawPrice.code               ?? "",
    active         :                rawPrice.active             ?? true,
    creationDate   : new Date     ( rawPrice.creationDate       ?? new Date()),
    basePrice      :                rawPrice.basePrice    / 100 ?? 0,
    taxRate        :                rawPrice.taxRate      / 100 ?? -1,
    priceWithTax   :                rawPrice.priceWithTax / 100 ?? 0, // ( rawPrice.basePrice && rawPrice.taxRate) ?  ( rawPrice.basePrice / 100 ) * ( 1 + rawPrice.taxRate / 10000 ) : 0,
    discounts      : mapDiscounts ( rawPrice.discounts          ?? []),
  }));

const mapVariants = (rawVariants: any[]): ProductVariant[] =>
  rawVariants.map((rawVariant) => ({
    ...initializedProductVariant(),
    id              :                      rawVariant.id              ?? 0,
    name            :                      rawVariant.name            ?? "",
    complement      :                      rawVariant.complement      ?? "",
    description     :                      rawVariant.description     ?? "",
    code            :                      rawVariant.code            ?? "",
    barcode         :                      rawVariant.barcode         ?? "",
    active          :                      rawVariant.active          ?? false,
    creationDate    : new Date           ( rawVariant.creationDate    ?? new Date() ),
    stockAvailable  :                      rawVariant.stockAvailable  ?? 0,
    stockBooked     :                      rawVariant.stockBooked     ?? 0,
    stockAssigned   :                      rawVariant.stockAssigned   ?? 0,
    stockThreshold  :                      rawVariant.stockThreshold  ?? 0,
    characteristics : mapCharacteristics ( rawVariant.characteristics ?? []),
    medias          : mapMedias          ( rawVariant.medias          ?? []),
    prices          : mapPrices          ( rawVariant.prices          ?? []),
    recurringIntervalUnit  :              rawVariant.recurringIntervalUnit  ?? null,
    recurringIntervalCount :              rawVariant.recurringIntervalCount ?? null,
    externalPaypalPlanId   :              rawVariant.externalPaypalPlanId   ?? null,
  }));


const defaultProduct = initializedProduct();


const mapProduct = (rawProduct: any): Product => ({
  ...defaultProduct,
  id                              :                      rawProduct.id                              ?? 0,
  uuid                            :                      rawProduct.uuid                            ?? '',
  publicRef                       :                      rawProduct.publicRef                            ?? '',
  name                            :                      rawProduct.name                            ?? '',
  catchword                       :                      rawProduct.catchword                       ?? "",
  code                            :                      rawProduct.code                            ?? "",
  publicPortal                    :                      rawProduct.publicPortal                    ?? false,
  active                          :                      rawProduct.active                          ?? false,
  activeScheduled                 :                      rawProduct.activeScheduled                 ?? false,
  activeStartDate                 :                      rawProduct.activeStartDate ? new Date(rawProduct.activeStartDate.date) : defaultProduct.activeStartDate,
  activeEndDate                   :                      rawProduct.activeEndDate ? new Date(rawProduct.activeEndDate.date) : defaultProduct.activeEndDate,
  creationDate                    :                      rawProduct.creationDate  ? new Date(rawProduct.creationDate.date) : defaultProduct.creationDate,  
  creationIpAddress               :                      rawProduct.creationIpAddress               ?? defaultProduct.creationIpAddress,
  requiredStatus                  :                      rawProduct.requiredStatus                  ?? "",
  requiredUserStatusId            :                      rawProduct.requiredUserStatusId            ?? 0,
  paymentRequired                 :                      rawProduct.paymentRequired                 ?? false,
  kind                            :                      rawProduct.kind                            ?? "",
  description                     :                      rawProduct.description                     ?? "",
  variants                        : mapVariants        ( rawProduct.variants                        ?? [] ),
  characteristics                 : mapCharacteristics ( rawProduct.characteristics                 ?? [] ),
  medias                          : mapMedias          ( rawProduct.medias                          ?? [] ),
  categoriesIds                   :                    ( rawProduct.productCategories               ?? [] ).map((category: any) => category.id ?? 0),
  associatedMediaId               :                      rawProduct.associatedMediaId               ?? 0,
  associatedBroadcastingChannelId :                      rawProduct.associatedBroadcastingChannelId ?? 0,
  associatedUserStatusId          :                      rawProduct.associatedUserStatusId          ?? 0,
  associatedAdminResource         :                      rawProduct.associatedAdminResource         ?? null,
});

const mapProducts = (rawProducts: any[]): Product[] => rawProducts.map(mapProduct);

export const getProducts = async (filter?: string, adminId?: number): Promise<BackendResponse_ProductList> => {

  const url          = API_URLS.getProducts;
  const queryParams  = new URLSearchParams();
  if (filter)  queryParams.append('filter', filter);
  if (adminId) queryParams.append('adminId', adminId.toString());
  const queryString = queryParams.toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;

  try
   {
    const response = await axios.get(fullUrl);


    if (response.status === 200 && Array.isArray(response.data))
     {
      const products = mapProducts(response.data);
/*
      for ( let p=0 ; p<products.length ; p++ )
	   {
	    if ( products[p].variants )
  	     {
		  if ( products[p].variants.length>0 )
		   {
		    if ( products[p].variants[0] )
		     {
			  if ( products[p].variants[0].prices )
			   {
  			    if ( products[p].variants[0].prices.length > 0 )
			     {
			      products[p].paymentRequired = true
				 };	
			   }
			 }
           }
         }
       }
*/

	       console.log ( 'getProducts', filter, response, products)

      return {
              code    : 1,
              comment : 'La récupération des produits depuis le serveur est terminée.',
              payload : products,
             };
    }

   return handleError<Product[]>(response.status, 'Réponse inattendue ou format incorrect de l\'API');
  }
 catch (error)
  {
   if (axios.isAxiosError(error))
    {
     return handleError<Product[]>(-101, error.response?.data?.detail ?? error.message);
    }

   return handleError<Product[]>(-102, 'Erreur inconnue lors de l\'appel à l\'API');
  }
};

export interface BackendResponse_ProductId extends BackendResponse<number> {}

export const createProduct = async ( product      : Product                             ,
                                     setProgress? : ((progress: number) => void) | null ,
									 adminId?     : number                              ): Promise<BackendResponse_ProductId> => {
  
  const url  = `${API_URLS.createProduct}`;

  console.log ('<>>>>>>>>>>>>>>>>>>> createProduct', url, product)


//  const urlT = 'https://01hmz6fc8we4p1x6901w664n1q10-cd8f448eeecae42b0655.requestinspector.com'

  const categoriesIdsStr: string = product.categoriesIds.join(',');

  const formData = new FormData();

  // Ajout des champs principaux du produit
                                               formData.append ( 'name'                            , product.name                                       );
  if (product.catchword)                       formData.append ( 'catchword'                       , product.catchword                                  );
  if (product.code)                            formData.append ( 'code'                            , product.code                                       );
                                               formData.append ( 'publicPortal'                    , product.publicPortal.toString()                    );
                                               formData.append ( 'active'                          , product.active.toString()                          );
                                               formData.append ( 'activeScheduled'                 , product.activeScheduled.toString()                  );
  if (product.activeStartDate)                 formData.append ( 'activeStartDate'                 , convertDateToString(product.activeStartDate)            );
  if (product.activeEndDate)                   formData.append ( 'activeEndDate'                   , convertDateToString(product.activeEndDate)            );
  if (product.requiredStatus)                  formData.append ( 'requiredStatus'                  , product.requiredStatus                             );
  if (product.requiredUserStatusId)            formData.append ( 'requiredUserStatusIdStr'         , product.requiredUserStatusId.toString()            );
                                               formData.append ( 'paymentRequired'                 , product.paymentRequired.toString()                 );
  if (product.kind)                            formData.append ( 'kind'                            , product.kind                                       );
  if (product.description)                     formData.append ( 'description'                     , product.description                                );
                                               formData.append ( 'categoriesIdsStr'                , categoriesIdsStr                                   );
  if (product.associatedMediaId)               formData.append ( 'associatedMediaId'               , product.associatedMediaId.toString()               );
  if (product.associatedBroadcastingChannelId) formData.append ( 'associatedBroadcastingChannelId' , product.associatedBroadcastingChannelId.toString() );
  if (product.associatedUserStatusId)          formData.append ( 'associatedUserStatusId'          , product.associatedUserStatusId.toString()          );
  if (product.associatedAdminResource)         formData.append ( 'associatedAdminResource'         , product.associatedAdminResource                    );
                                               formData.append ( 'variants.length'                 , product.variants.length.toString()                 );
                                               formData.append ( 'characteristics.length'          , product.characteristics.length.toString()          );
                                               formData.append ( 'medias.length'                   , product.medias.length.toString()                   );

  if (adminId)                                 formData.append ( 'adminId'                         , adminId.toString()               );

  // Ajout des caractéristiques du produit
  product.characteristics.forEach((characteristic, index) => {
    if (characteristic.name)                 formData.append ( `characteristics.${index}.name`        , characteristic.name            );
    if (characteristic.kind)                 formData.append ( `characteristics.${index}.kind`        , characteristic.kind            );
    if (characteristic.unit)                 formData.append ( `characteristics.${index}.unit`        , characteristic.unit            );
    if (characteristic.value)                formData.append ( `characteristics.${index}.value`       , characteristic.value           );
    if (characteristic.rank)                 formData.append ( `characteristics.${index}.rank`        , characteristic.rank.toString() );
    if (characteristic.tag)                  formData.append ( `characteristics.${index}.tag`         , characteristic.tag           );
  });

  // Ajout des caractéristiques du produit (y compris photos)
  product.medias.forEach((media, index) => {
    if (media.name)                 formData.append ( `medias.${index}.name`        , media.name                   );
    if (media.rank)                 formData.append ( `medias.${index}.rank`        , media.rank.toString()        );
    if (media.asThumbnail)          formData.append ( `medias.${index}.asThumbnail` , media.asThumbnail.toString() );
    if (media.mediaType)            formData.append ( `medias.${index}.mediaType`   , media.mediaType              );
    if (media.fileContent !== null) formData.append ( `medias.${index}.fileContent` , media.fileContent            );
    if (media.tag)                  formData.append ( `medias.${index}.tag`         , media.tag                    );
  });

  // Ajout des variantes du produit
  product.variants.forEach((variant, variantIndex) => {
    if (variant.name)        formData.append ( `variants.${variantIndex}.name`                   , variant.name                      );
    if (variant.complement)  formData.append ( `variants.${variantIndex}.complement`             , variant.complement                );
    if (variant.description) formData.append ( `variants.${variantIndex}.description`            , variant.description               );
    if (variant.code)        formData.append ( `variants.${variantIndex}.code`                   , variant.code                      );
    if (variant.barcode)     formData.append ( `variants.${variantIndex}.barcode`                , variant.barcode                   );
                             formData.append ( `variants.${variantIndex}.active`                 , variant.active.toString()         );
                             formData.append ( `variants.${variantIndex}.stockAvailable`         , variant.stockAvailable.toString() );
                             formData.append ( `variants.${variantIndex}.stockBooked`            , variant.stockBooked.toString()    );
                             formData.append ( `variants.${variantIndex}.stockAssigned`          , variant.stockAssigned.toString()  );
                             formData.append ( `variants.${variantIndex}.stockThreshold`         , variant.stockThreshold.toString() );

                             formData.append ( `variants.${variantIndex}.characteristics.length` , variant.characteristics.length.toString() );
                             formData.append ( `variants.${variantIndex}.medias.length`          , variant.medias.length.toString() );
                             formData.append ( `variants.${variantIndex}.prices.length`          , variant.prices.length.toString()          );

    // Ajout des caractéristiques des variantes
    variant.characteristics.forEach((charac, characIndex) => {
      if (charac.name)                 formData.append ( `variants.${variantIndex}.characteristics.${characIndex}.name`        , charac.name            );
      if (charac.kind)                 formData.append ( `variants.${variantIndex}.characteristics.${characIndex}.kind`        , charac.kind            );
      if (charac.unit)                 formData.append ( `variants.${variantIndex}.characteristics.${characIndex}.unit`        , charac.unit            );
      if (charac.value)                formData.append ( `variants.${variantIndex}.characteristics.${characIndex}.value`       , charac.value           );
      if (charac.rank)                 formData.append ( `variants.${variantIndex}.characteristics.${characIndex}.rank`        , charac.rank.toString() );
      if (charac.tag)                  formData.append ( `variants.${variantIndex}.characteristics.${characIndex}.tag`         , charac.tag             );
    });

    // Ajout des medias des variantes
    variant.medias.forEach((media, mediaIndex) => {
      if (media.name)                 formData.append ( `variants.${variantIndex}.medias.${mediaIndex}.name`        , media.name                   );
      if (media.rank)                 formData.append ( `variants.${variantIndex}.medias.${mediaIndex}.rank`        , media.rank.toString()        );
      if (media.asThumbnail)          formData.append ( `variants.${variantIndex}.medias.${mediaIndex}.asThumbnail` , media.asThumbnail.toString() );
      if (media.mediaType)            formData.append ( `variants.${variantIndex}.medias.${mediaIndex}.mediaType`   , media.mediaType              );
      if (media.fileContent !== null) formData.append ( `variants.${variantIndex}.medias.${mediaIndex}.fileContent` , media.fileContent            );
      if (media.tag)                  formData.append ( `variants.${variantIndex}.medias.${mediaIndex}.tag`         , media.tag                    );
    });

    // Ajout des prix des variantes
    variant.prices.forEach((price, priceIndex) => {
      if (price.name)           formData.append ( `variants.${variantIndex}.prices.${priceIndex}.name`             ,   price.name                            );
      if (price.requiredStatus) formData.append ( `variants.${variantIndex}.prices.${priceIndex}.requiredStatus`   ,   price.requiredStatus                  );
      if (price.comment)        formData.append ( `variants.${variantIndex}.prices.${priceIndex}.comment`          ,   price.comment                         );
      if (price.code)           formData.append ( `variants.${variantIndex}.prices.${priceIndex}.code`             ,   price.code                            );
                                formData.append ( `variants.${variantIndex}.prices.${priceIndex}.active`           ,   price.active              .toString() );
                                formData.append ( `variants.${variantIndex}.prices.${priceIndex}.basePrice`        , ( price.basePrice     ).toString() );
                                formData.append ( `variants.${variantIndex}.prices.${priceIndex}.taxRate`          , ( price.taxRate       ).toString() );
                                formData.append ( `variants.${variantIndex}.prices.${priceIndex}.priceWithTax`     , ( price.priceWithTax  ).toString() );
                                formData.append ( `variants.${variantIndex}.prices.${priceIndex}.discounts.length` ,   price.discounts.length    .toString() );
								
      // Ajout des remises des prix
      price.discounts.forEach((discount, discountIndex) => {
        formData.append ( `variants.${variantIndex}.prices.${priceIndex}.discounts.${discountIndex}.type`           , discount.type                  );
        formData.append ( `variants.${variantIndex}.prices.${priceIndex}.discounts.${discountIndex}.condition`      , discount.condition             );
        formData.append ( `variants.${variantIndex}.prices.${priceIndex}.discounts.${discountIndex}.value`          , discount.value                 );
        formData.append ( `variants.${variantIndex}.prices.${priceIndex}.discounts.${discountIndex}.active`         , discount.active.toString()     );
        formData.append ( `variants.${variantIndex}.prices.${priceIndex}.discounts.${discountIndex}.priceToApplyOn` , discount.priceToApplyOn        );
        formData.append ( `variants.${variantIndex}.prices.${priceIndex}.discounts.${discountIndex}.priceToPay`     , discount.priceToPay.toString() );
      });
    });
  });

  const userAgentInformations = getUserAgentInformations ();
  const appInformations       = getAppInformations ();
	
  try
   {
    const response = await axios.post (
                                          url      ,
                                          formData ,
                                        { headers: { 
										             'Accept': 'application/ld+json', 
										             'Content-Type': 'multipart/form-data' ,
												     'User-Agent-Informations': `${userAgentInformations}`,
	                                                 'App-Informations': `${appInformations}`,
												   }   ,
                                          onUploadProgress: (event) => {

		                                        if (event.total)
												 {
                                                  const percentCompleted = Math.round((event.loaded * 100) / event.total);

                                                  if ( setProgress && typeof (setProgress) !== "undefined" )
												   {
                                                    setProgress(percentCompleted);
												   }
                                                 }
                                              }}
                                      );

    console.log (response)

    if (response.status === 201)
          {
           return {
                    code    : 1,
                    comment : 'Le produit a été créé sur le serveur',
                    payload : response.data.id
                  };
          }
     else {
           return handleError<number>(response.status, 'Error creating the product');
          }
   }
  catch (error)
   {
    if (axios.isAxiosError(error))
     {
      return handleError<number>(-101, error.response?.data?.detail ?? error.message);
     }

    return handleError<number>(-102, 'Unknown error occurred');
   }
};

export const updateProduct = async ( product      : Product                             ,
                                     setProgress? : ((progress: number) => void) | null ,
									 adminId?     : number                              ) => {

  const url = `${API_URLS.patchProduct}`;
 // const url     = baseUrl.replace('{id}', `${product.id}`);

  console.log ('<>>>>>>>>>>>>>>>>>>> updateProduct', url, product)

  const categoriesIdsStr: string = product.categoriesIds.join(',');

  const formData = new FormData();

  // Ajout des champs principaux du produit
                                               formData.append ( 'id'                              , product.id.toString()                              );
                                               formData.append ( 'uuid'                            , product.uuid                                       );
                                               formData.append ( 'name'                            , product.name                                       );
  if (product.catchword)                       formData.append ( 'catchword'                       , product.catchword                                  );
  if (product.code)                            formData.append ( 'code'                            , product.code                                       );
                                               formData.append ( 'publicPortal'                    , product.publicPortal.toString()                    );
                                               formData.append ( 'active'                          , product.active.toString()                          );
                                               formData.append ( 'activeScheduled'                 , product.activeScheduled.toString()                  );
  if (product.activeStartDate)                 formData.append ( 'activeStartDate'                 , convertDateToString(product.activeStartDate)            );
  if (product.activeEndDate)                   formData.append ( 'activeEndDate'                   , convertDateToString(product.activeEndDate)            );
  if (product.requiredStatus)                  formData.append ( 'requiredStatus'                  , product.requiredStatus                             );
  if (product.requiredUserStatusId)            formData.append ( 'requiredUserStatusIdStr'         , product.requiredUserStatusId.toString()            );
                                               formData.append ( 'paymentRequired'                 , product.paymentRequired.toString()                 );
  if (product.kind)                            formData.append ( 'kind'                            , product.kind                                       );
  if (product.description)                     formData.append ( 'description'                     , product.description                                );
                                               formData.append ( 'categoriesIdsStr'                , categoriesIdsStr                                   );
  if (product.associatedMediaId)               formData.append ( 'associatedMediaId'               , product.associatedMediaId.toString()               );
  if (product.associatedBroadcastingChannelId) formData.append ( 'associatedBroadcastingChannelId' , product.associatedBroadcastingChannelId.toString() );
  if (product.associatedUserStatusId)          formData.append ( 'associatedUserStatusId'          , product.associatedUserStatusId.toString()          );
  if (product.associatedAdminResource)         formData.append ( 'associatedAdminResource'         , product.associatedAdminResource                    );
                                               formData.append ( 'variants.length'                 , product.variants.length.toString()                 );
                                               formData.append ( 'characteristics.length'          , product.characteristics.length.toString()          );
                                               formData.append ( 'medias.length'                   , product.medias.length.toString()          );

  if (adminId)                                 formData.append ( 'adminId'                         , adminId.toString()               );

  // Ajout des caractéristiques du produit
  product.characteristics.forEach((characteristic, index) => {
                                             formData.append ( `characteristics.${index}.id`          , characteristic.id.toString() );
    if (characteristic.name)                 formData.append ( `characteristics.${index}.name`        , characteristic.name          );
    if (characteristic.kind)                 formData.append ( `characteristics.${index}.kind`        , characteristic.kind          );
    if (characteristic.unit)                 formData.append ( `characteristics.${index}.unit`        , characteristic.unit          );
    if (characteristic.value)                formData.append ( `characteristics.${index}.value`       , characteristic.value         );
    if (characteristic.rank)                 formData.append ( `characteristics.${index}.rank`        , characteristic.rank.toString() );
    if (characteristic.tag)                  formData.append ( `characteristics.${index}.tag`         , characteristic.tag           );
  });

  // Ajout des medias du produit
  product.medias.forEach((media, index) => {
                                    formData.append ( `medias.${index}.id`          , media.id.toString() );
    if (media.name)                 formData.append ( `medias.${index}.name`        , media.name                   );
    if (media.rank)                 formData.append ( `medias.${index}.rank`        , media.rank.toString()        );
    if (media.asThumbnail)          formData.append ( `medias.${index}.asThumbnail` , media.asThumbnail.toString() );
    if (media.mediaType)            formData.append ( `medias.${index}.mediaType`   , media.mediaType              );
    if (media.fileContent !== null) formData.append ( `medias.${index}.fileContent` , media.fileContent            );
    if (media.tag)                  formData.append ( `medias.${index}.tag`         , media.tag                    );
  });

  // Ajout des variantes du produit
  product.variants.forEach((variant, variantIndex) => {
                             formData.append ( `variants.${variantIndex}.id`                     , variant.id.toString()             );
    if (variant.name)        formData.append ( `variants.${variantIndex}.name`                   , variant.name                      );
    if (variant.complement)  formData.append ( `variants.${variantIndex}.complement`             , variant.complement                );
    if (variant.description) formData.append ( `variants.${variantIndex}.description`            , variant.description               );
    if (variant.code)        formData.append ( `variants.${variantIndex}.code`                   , variant.code                      );
    if (variant.barcode)     formData.append ( `variants.${variantIndex}.barcode`                , variant.barcode                   );
                             formData.append ( `variants.${variantIndex}.active`                 , variant.active.toString()         );
                             formData.append ( `variants.${variantIndex}.stockAvailable`         , variant.stockAvailable.toString() );
                             formData.append ( `variants.${variantIndex}.stockBooked`            , variant.stockBooked.toString()    );
                             formData.append ( `variants.${variantIndex}.stockAssigned`          , variant.stockAssigned.toString()  );
                             formData.append ( `variants.${variantIndex}.stockThreshold`         , variant.stockThreshold.toString() );

                             formData.append ( `variants.${variantIndex}.characteristics.length` , variant.characteristics.length.toString() );
                             formData.append ( `variants.${variantIndex}.medias.length`          , variant.medias.length.toString() );
                             formData.append ( `variants.${variantIndex}.prices.length`          , variant.prices.length.toString()          );

    // Ajout des caractéristiques des variantes
    variant.characteristics.forEach((charac, characIndex) => {
                                       formData.append ( `variants.${variantIndex}.characteristics.${characIndex}.id`          , charac.id.toString() );
      if (charac.name)                 formData.append ( `variants.${variantIndex}.characteristics.${characIndex}.name`        , charac.name          );
      if (charac.kind)                 formData.append ( `variants.${variantIndex}.characteristics.${characIndex}.kind`        , charac.kind          );
      if (charac.unit)                 formData.append ( `variants.${variantIndex}.characteristics.${characIndex}.unit`        , charac.unit          );
      if (charac.value)                formData.append ( `variants.${variantIndex}.characteristics.${characIndex}.value`       , charac.value         );
      if (charac.rank)                 formData.append ( `variants.${variantIndex}.characteristics.${characIndex}.rank`        , charac.rank.toString() );
      if (charac.tag)                  formData.append ( `variants.${variantIndex}.characteristics.${characIndex}.tag`         , charac.tag           );
    });

    // Ajout des medias des variantes
    variant.medias.forEach((media, mediaIndex) => {
                                      formData.append ( `variants.${variantIndex}.medias.${mediaIndex}.id`          , media.id.toString()          );
      if (media.name)                 formData.append ( `variants.${variantIndex}.medias.${mediaIndex}.name`        , media.name                   );
      if (media.rank)                 formData.append ( `variants.${variantIndex}.medias.${mediaIndex}.rank`        , media.rank.toString()        );
      if (media.asThumbnail)          formData.append ( `variants.${variantIndex}.medias.${mediaIndex}.asThumbnail` , media.asThumbnail.toString() );
      if (media.mediaType)            formData.append ( `variants.${variantIndex}.medias.${mediaIndex}.mediaType`   , media.mediaType              );
      if (media.fileContent !== null) formData.append ( `variants.${variantIndex}.medias.${mediaIndex}.fileContent` , media.fileContent            );
      if (media.tag)                  formData.append ( `variants.${variantIndex}.medias.${mediaIndex}.tag`         , media.tag                    );
   });

    // Ajout des prix des variantes
    variant.prices.forEach((price, priceIndex) => {
                                formData.append ( `variants.${variantIndex}.prices.${priceIndex}.id`               ,   price.id.toString()                   );
      if (price.name)           formData.append ( `variants.${variantIndex}.prices.${priceIndex}.name`             ,   price.name                            );
      if (price.requiredStatus) formData.append ( `variants.${variantIndex}.prices.${priceIndex}.requiredStatus`   ,   price.requiredStatus                  );
      if (price.comment)        formData.append ( `variants.${variantIndex}.prices.${priceIndex}.comment`          ,   price.comment                         );
      if (price.code)           formData.append ( `variants.${variantIndex}.prices.${priceIndex}.code`             ,   price.code                            );
                                formData.append ( `variants.${variantIndex}.prices.${priceIndex}.active`           ,   price.active              .toString() );
                                formData.append ( `variants.${variantIndex}.prices.${priceIndex}.basePrice`        , ( price.basePrice     ).toString() );
                                formData.append ( `variants.${variantIndex}.prices.${priceIndex}.taxRate`          , ( price.taxRate       ).toString() );
                                formData.append ( `variants.${variantIndex}.prices.${priceIndex}.priceWithTax`     , ( price.priceWithTax  ).toString() );
                                formData.append ( `variants.${variantIndex}.prices.${priceIndex}.discounts.length` ,   price.discounts.length    .toString() );

      // Ajout des remises des prix
      price.discounts.forEach((discount, discountIndex) => {
        formData.append ( `variants.${variantIndex}.prices.${priceIndex}.discounts.${discountIndex}.id`             , discount.id.toString()     );
        formData.append ( `variants.${variantIndex}.prices.${priceIndex}.discounts.${discountIndex}.type`           , discount.type              );
        formData.append ( `variants.${variantIndex}.prices.${priceIndex}.discounts.${discountIndex}.condition`      , discount.condition         );
        formData.append ( `variants.${variantIndex}.prices.${priceIndex}.discounts.${discountIndex}.value`          , discount.value             );
        formData.append ( `variants.${variantIndex}.prices.${priceIndex}.discounts.${discountIndex}.active`         , discount.active.toString() );
        formData.append ( `variants.${variantIndex}.prices.${priceIndex}.discounts.${discountIndex}.priceToApplyOn` , discount.priceToApplyOn    );
        formData.append ( `variants.${variantIndex}.prices.${priceIndex}.discounts.${discountIndex}.priceToPay`     , discount.priceToPay.toString() );
      });
    });
  });

  // Envoi de la requête
  try
   {
    const response = await axios.post(url, formData, {
      headers: { 'Accept': 'application/ld+json', 'Content-Type': 'multipart/form-data' }  ,
                                           onUploadProgress: (event) => {

		                                        if (event.total)
												 {
                                                  const percentCompleted = Math.round((event.loaded * 100) / event.total);

                                                  if ( setProgress && typeof (setProgress) !== "undefined" )
												   {
                                                    setProgress(percentCompleted);
												   }
                                                 }
                                              }
    });

    console.log (response)

    if ( response.status === 201 ) {
      return {
        code: 1,
        comment: 'Product updated successfully',
              payload : {} as any
      };
    } else {
      return handleError<void>(response.status, 'Error updating the product');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Erreur inconne');
  }
};

export const deleteProduct = async (id: number): Promise<BackendResponse> => {

  const baseUrl = `${API_URLS.deleteProduct}`;
  const url     = baseUrl.replace('{id}', `${id}`);

  try
   {
    const response = await axios.delete(url);

    if (response.status === 204)  // Suppression réussie, sans contenu retourné
          {
           return {
                    code    : 1,
                    comment : 'Le produit a été supprimé du serveur.',
                    payload : {} as any
                  };
          }
     else {
           return handleError<void>(response.status, 'Erreur durant la tentative de suppression du produit');
          }
   }
  catch (error)
   {
    if (axios.isAxiosError(error))
     {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
     }

    return handleError<void>(-102, 'Erreur inconnue');
   }
};

export const updateProductVisibility = async ( id         : number  ,
                                               visibility : boolean ): Promise<BackendResponse> => {

  const baseUrl       = `${API_URLS.patchProductVisibility}`;
  const url           = baseUrl.replace('{id}', `${id}`);
  const visibilityStr = visibility ? 'true' : 'false';

  const headers = {
                    'Accept'       : 'application/ld+json'          ,
                    'Content-Type' : 'application/merge-patch+json'
                  };

  try
   {
    const response = await axios.patch ( url , { visibility: visibilityStr } , { headers } );

    if (response.status === 200 || response.status === 204)
          {
           return {
                    code    : 1,
                    comment : 'La visibilité du produit a été changé sur le serveur.',
                    payload : {} as any
                  };
          }
     else {
           return handleError<void>(response.status, 'Non-200/204 response from API');
          }
   }
  catch (error)
   {
    if (axios.isAxiosError(error))
     {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
     }

    return handleError<void>(-102, 'Erreur inconnue');
   }
};

export interface BackendResponse_ProductAndVariantExistence extends BackendResponse<boolean> {}

export const getProductAndVariantExistenceCheck = async ( rawField:   string         , 
                                                          rawValue:   string         ,
														  forProduct: boolean = true ): Promise<BackendResponse_ProductAndVariantExistence> => {

    const baseUrl  =  `${API_URLS.getProductAndVariantExistenceCheck}?`;

    const field = encodeURIComponent (rawField);      
    const value = encodeURIComponent (rawValue); 

    var url = `${baseUrl}field=${field}&value=${value}&forProduct=${forProduct.toString()}`;

/*	const userAgentInformations = getUserAgentInformations ();
	const appInformations       = getAppInformations ();

    const headers = {
	    'User-Agent-Informations': `${userAgentInformations}`,
	    'App-Informations': `${appInformations}`,
    };
	*/	  
	
    var result:BackendResponse_ProductAndVariantExistence = {
      code:    0,
      comment: "",
	  payload: false,
    };

    try {

		const response = await axios.get ( url ); //, { headers: headers } );
		
		const statut   = response.status;
		const exist    = response.data['hydra:member'][0].exist;

		if ( statut === 200 )
		      {
			   if (exist === "true" ) 
		             {
                      result = {
                        code:    1,
                        comment: "Produit/Variant trouvé",
						payload: true
					   }
                     }
                else {
                      result = {
                        code:    1,
                        comment: "Produit/Variant non trouvé",
						payload: false
                      };        
				     }			   
		      }
		 else {
               result = {
                 code:    -101,
                 comment: `Erreur lors de la recherche du produit/variant : ${response.status}`,
				 payload: false
               };        
		      }
    } 
    catch (err) {

        result = {
		  code:    -201,
		  comment: "Erreur réseau",
		  payload: false
		};
    }

    return result;
};

// ── Abonnements récurrents : provisionnement Paypal ──────────────────────────────────
//
// Paypal exige un Billing Plan pré-existant avant de pouvoir créer un abonnement dessus
// (contrairement à Stripe, qui construit son prix récurrent à la volée) — cf.
// ProductVariantCreatePaypalPlan.php. Bouton "Créer le plan Paypal" dans
// ProductSheet_ProductVariant, visible seulement sur un palier récurrent sans plan.

export interface BackendResponse_PaypalPlanId extends BackendResponse<string> {}

export const createPaypalPlanForVariant = async (variantId: number): Promise<BackendResponse_PaypalPlanId> => {

  const url = API_URLS.postProductVariantCreatePaypalPlan.replace('{id}', `${variantId}`);

  try {
    const response = await axios.post(url);

    if (response.status === 200 && response.data?.externalPaypalPlanId) {
      return { code: 1, comment: "Plan Paypal créé.", payload: response.data.externalPaypalPlanId };
    }
    return handleError<string>(response.status, 'Réponse non-200 ou plan manquant.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<string>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<string>(-102, 'Erreur inconnue.');
  }
};
